<template>
  <v-container class="py-0 my-0">
    <PageHeader :title="pageTitle" :path="goToPath" />
    <v-card elevation="1">
      <v-container class="mobile-container">
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="search"
              :append-icon="searchIcon"
              label="Buscar"
              single-line
              hide-details
              variant="underlined"
              clearable
              id="search-bar"
            ></v-text-field>
          </v-col>
          <v-col
            cols="6"
            class="d-flex justify-end"
            :class="isMobile ? 'mt-4' : 'mt-2'"
          >
            <v-btn
              color="primary"
              variant="flat"
              @click="openImportDialog()"
              id="v-btn-nueva-importacion"
              :prepend-icon="addIcon"
            >
              Nueva importación
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
      <v-card-text class="py-0 my-0">
        <v-row>
          <v-col cols="12" class="px-0 mx-0">
            <v-data-table
              :items-per-page="itemsPerPage"
              :headers="isMobile ? headersMobile : headers"
              :mobile="isMobile"
              :hide-default-header="isMobile"
              class="pa-3"
              :items="importaciones"
              :search="search"
              :loading="isDataTableLoading"
            >
              <template v-slot:[`item.fechaImportacion`]="{ item }">
                {{ formatDate(item.fechaImportacion) }}
              </template>
              <template v-slot:[`item.estado`]="{ item }">
                <v-chip
                  :color="getEstadoColor(item.estado)"
                  variant="flat"
                  size="small"
                >
                  {{ item.estado }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip text="Ver detalles" location="start">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      size="20"
                      :disabled="item.registrosExitosos === 0"
                      @click="verDetalles(item)"
                      v-bind="props"
                      id="v-icon-ver-detalles"
                    >
                      {{ seeIcon }}
                    </v-icon>
                  </template>
                </v-tooltip>
                <v-tooltip text="Revertir importación" location="start">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      size="20"
                      @click="revertir(item)"
                      v-bind="props"
                      id="v-icon-revertir"
                      :disabled="
                        item.registrosExitosos === 0 ||
                        item.estado === 'Revertido'
                      "
                    >
                      {{ undoIcon }}
                    </v-icon>
                  </template>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Modal para importar según el tipo de entidad -->
    <v-dialog
      v-if="isOpenModalImport"
      v-model="isOpenModalImport"
      :max-width="isMobile ? '95%' : '45%'"
      persistent
    >
      <ImportarFarmacia
        v-if="tipoEntidad === 'Farmacia'"
        :financiadorId="financiadorId"
        @closeDialog="closeImportDialog"
        @reloadFarmacias="loadImportaciones"
      />
      <ImportarEntidadesEmisoras
        v-else-if="tipoEntidad === 'Entidad emisora'"
        :financiadorId="financiadorId"
        @closeDialog="closeImportDialog"
        @reloadEmisores="loadImportaciones"
      />
      <ImportarMedicosEmisores
        v-else-if="tipoEntidad === 'Medico emisor'"
        :financiadorId="financiadorId"
        @closeDialog="closeImportDialog"
        @reloadMedicosEmisores="loadImportaciones"
      />
    </v-dialog>

    <!-- Modal para ver detalles -->
    <v-dialog
      v-if="dialogDetalles"
      v-model="dialogDetalles"
      :max-width="isMobile ? '95%' : '83%'"
      persistent
    >
      <VerDetalleImportacion
        :importacionItem="selectedImportacion"
        :tipoEntidad="tipoEntidad"
        :financiadorId="financiadorId"
        :financiadorNombre="getFinanciadorNom"
        @closeDialog="closeDetallesDialog"
      />
    </v-dialog>
    <!-- Confirm Dialog para revertir importación -->
    <v-dialog
      :max-width="isMobile ? '95%' : '45%'"
      v-model="showConfirmDialog"
      @keydown.esc="showConfirmDialog = false"
      persistent
    >
      <ConfirmDialog
        v-if="showConfirmDialog"
        :isLoading="isLoadingReversion"
        :title="confirmDialogTitle"
        @confirmar="confirmarReversion"
        @confirmar-cancelado="showConfirmDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import PageHeader from "@/components/layout/PageHeader.vue";
import enums from "@/utils/enums/index";
import { mapActions, mapGetters } from "vuex";
import ImportarFarmacia from "@/components/modules/farmacias/ImportarFarmacia.vue";
import ImportarEntidadesEmisoras from "@/components/modules/emisores/ImportarEntidadesEmisoras.vue";
import ImportarMedicosEmisores from "@/components/modules/financiadores/ImportarMedicosEmisores.vue";
import ConfirmDialog from "@/components/shared/ConfirmDialog.vue";
import VerDetalleImportacion from "@/components/modules/importaciones/VerDetalleImportacion.vue";

export default {
  name: "ConsultaImportaciones",
  components: {
    PageHeader,
    ImportarFarmacia,
    ImportarEntidadesEmisoras,
    ImportarMedicosEmisores,
    ConfirmDialog,
    VerDetalleImportacion,
  },
  props: {
    tipoEntidad: {
      type: String,
      required: true,
      validator: (value) =>
        ["Farmacia", "Entidad emisora", "Medico emisor"].includes(value),
    },
  },
  data() {
    return {
      search: "",
      itemsPerPage: 10,
      isDataTableLoading: false,
      isOpenModalImport: false,
      dialogDetalles: false,
      importaciones: [], // Temporal, luego vendrá del store
      selectedImportacion: null,
      financiadorId: null,
      searchIcon: enums.icons.SEARCH_ICON,
      addIcon: enums.icons.ADD_ICON,
      seeIcon: enums.icons.SEE,
      undoIcon: enums.icons.UNDO,
      closeIcon: enums.icons.CLOSE_ICON,
      // Confirm Dialog
      showConfirmDialog: false,
      confirmDialogTitle: "",
      importacionToRevert: null,
      isLoadingReversion: false,
      headers: [
        {
          title: "Fecha",
          value: "fechaImportacion",
          align: "start",
          sortable: true,
        },
        { title: "Usuario", value: "usuario", align: "start", sortable: true },
        {
          title: "Archivo",
          value: "nombreArchivo",
          align: "center",
          sortable: true,
        },
        {
          title: "Registros Exitosos",
          value: "registrosExitosos",
          align: "center",
          sortable: true,
        },
        { title: "Estado", value: "estado", align: "center", sortable: true },
        { title: "Acciones", value: "actions", align: "end", sortable: false },
      ],
      headersMobile: [
        { title: "Fecha", value: "fechaImportacion" },
        { title: "Estado", value: "estado" },
        { title: "Acciones", value: "actions" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      isMobile: "menu/isMobile",
      getFinanciadorId: "financiador/getFinanciadorId",
      getFinanciadorNom: "financiador/getFinanciadorNom",
    }),
    pageTitle() {
      const tipoConTilde =
        this.tipoEntidad === "Medico emisor"
          ? "Médico Emisor"
          : this.tipoEntidad;
      const baseTitle = `Importaciones de ${tipoConTilde}`;
      return this.getFinanciadorNom
        ? `${baseTitle} - ${this.getFinanciadorNom}`
        : baseTitle;
    },
    goToPath() {
      // Retroceder a la pantalla anterior según el tipo de entidad
      switch (this.tipoEntidad) {
        case "Farmacia":
          return "/financiadores/gestion-farmacias";
        case "Entidad emisora":
          return "/emisores";
        case "Medico emisor":
          return "/financiadores/gestion-usuarios-emisores";
        default:
          return "/";
      }
    },

  },
  mounted() {
    this.initializeData();
  },
  methods: {
    ...mapActions({
      setAlert: "user/setAlert",
      getImportacionesByEntidad: "farmacia/getImportacionesByEntidad",
      revertirImportacion: "farmacia/revertirImportacion",
    }),
    initializeData() {
      // Obtener el financiadorId del sessionStorage o store
      this.financiadorId = this.getFinanciadorId; // Asigno el dato con el financiador cargado en el state
      this.loadImportaciones();
    },
    async loadImportaciones() {
      this.isDataTableLoading = true;
      try {
        const response = await this.getImportacionesByEntidad({
          financiadorId: this.financiadorId,
          tipoEntidad: this.tipoEntidad,
        });
        // Mapear las propiedades del DTO de C# al formato camelCase del frontend
        this.importaciones = (response || []).map((item) => ({
          importacionId: item.ImportacionId || item.importacionId,
          fechaImportacion: item.FechaImportacion || item.fechaImportacion,
          usuario: item.Usuario || item.usuario,
          nombreArchivo: item.NombreArchivo || item.nombreArchivo,
          registrosExitosos: item.RegistrosExitosos || item.registrosExitosos,
          estado: item.Estado || item.estado,
          tipoEntidad: item.TipoEntidad || item.tipoEntidad,
          financiadorId: item.FinanciadorId || item.financiadorId,
        }));
      } catch (error) {
        console.error("Error loading importaciones:", error);
        this.setAlert({
          type: "error",
          message: "Error al cargar las importaciones",
        });
      } finally {
        this.isDataTableLoading = false;
      }
    },
    openImportDialog() {
      this.isOpenModalImport = true;
    },
    closeImportDialog() {
      this.isOpenModalImport = false;
    },
    verDetalles(item) {
      this.selectedImportacion = item;
      this.dialogDetalles = true;
    },
    closeDetallesDialog() {
      this.dialogDetalles = false;
      this.selectedImportacion = null;
    },
    revertir(item) {
      this.importacionToRevert = item;
      this.confirmDialogTitle = `¿Está seguro que desea revertir la importación?`;
      this.showConfirmDialog = true;
    },
    async confirmarReversion() {
      this.isLoadingReversion = true;
      try {
        if (!this.importacionToRevert) return;

        const response = await this.revertirImportacion({
          importacionId: this.importacionToRevert.importacionId,
          tipoEntidad: this.tipoEntidad,
          financiadorId: this.financiadorId,
        });
        if (response && response.status === 200) {
          this.setAlert({
            type: "success",
            message: "Importación revertida exitosamente",
          });
        }
        // Recargar la lista de importaciones
        await this.loadImportaciones();
      } catch (error) {
        this.setAlert({
          type: "warning",
          message: error,
        });
      } finally {
        this.isLoadingReversion = false;
        this.showConfirmDialog = false;
        this.importacionToRevert = null;
        this.confirmDialogTitle = "";
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getEstadoColor(estado) {
      switch (estado) {
        case "Completado sin errores":
        case "Completada":
          return "success";
        case "Completado con errores":
        case "Con errores":
          return "warning";
        case "No completado":
        case "Fallida":
          return "error";
        case "Revertido":
          return "grey";
        default:
          return "primary";
      }
    },
  },
};
</script>